import type { Property } from '../../../../core/view-manager/property.js';
import type { GanttSingleView } from '../../gantt-view-manager.js';

/**
 * Heuristic matches for the four columns the wizard configures. A match
 * is "the existing column we'd bet the user means by that role" — name
 * + type + (for relations) self-targeting. When present, the wizard
 * flips its toggle ON by default and pre-selects the match so the
 * common case ("I already have these columns, just wire them up")
 * needs zero clicks.
 *
 * Fields are independent: start and end can match separately, e.g. a
 * schema with only "Due date" will match `end` without a `start`.
 */
export interface DetectedMatches {
  readonly start: Property | undefined;
  readonly end: Property | undefined;
  readonly progress: Property | undefined;
  readonly status: Property | undefined;
  readonly assignee: Property | undefined;
  readonly dependencies: Property | undefined;
}

export interface DetectedColumns {
  readonly date: Property[];
  /** Progress-typed columns only. Number columns are NOT eligible for the progress role. */
  readonly progress: Property[];
  /** Select-typed columns. Feed the status role (colour bars by status). */
  readonly status: Property[];
  /** Member-typed columns (sl-canvas2 extension). Feed the assignee role. */
  readonly assignee: Property[];
  /** Only relation columns that point to the current database (self-relations). */
  readonly relation: Property[];
  /** Block id of the owning database, resolved from the data source. Empty when unknown. */
  readonly selfBlockId: string;
  /** Name+type heuristics. See {@link DetectedMatches}. */
  readonly match: DetectedMatches;
}

export type WizardCase = 'no-date' | 'one-date' | 'many-dates';

/** Heuristic name patterns for each role. Tested case-insensitively. */
const NAME_PATTERNS = {
  start: /\b(start|begin|from|kick.?off)\b/i,
  end: /\b(end|finish|due|to|deadline|target)\b/i,
  progress: /\b(progress|percent|complete|completion|done|%)\b/i,
  status: /\b(status|state|phase|stage)\b/i,
  assignee: /\b(assign(ee|ed)?|owner|responsible|lead|user|member|person)\b/i,
  dependencies:
    /\b(depend(enc(y|ies))?|block(ed|s|ing)?|predecessor|successor|requires)\b/i,
};

/**
 * Buckets the properties of a gantt view by the column types the
 * wizard cares about: date (for start/end), progress-typed (for
 * progress), and relation (for dependencies). Plain number columns
 * are NOT eligible for the progress role — the dedicated progress
 * type exists for a reason and a free-form number column has no
 * contract on the 0…1 / 0…100 scale the bar renderer expects.
 *
 * Dependency relations MUST target the same database — a relation column
 * that points elsewhere cannot represent task→task dependencies because
 * the cell values would be row ids in the other database. We therefore
 * filter out cross-doc / cross-db relations at detection time so the
 * wizard only ever offers valid self-relations.
 */
export class ColumnDetectionService {
  static scan(view: GanttSingleView): DetectedColumns {
    const date: Property[] = [];
    const progress: Property[] = [];
    const status: Property[] = [];
    const assignee: Property[] = [];
    const relation: Property[] = [];
    const selfBlockId = resolveSelfBlockId(view);

    for (const property of view.getBoundedRawPropertiesWindow().properties) {
      // Skip read-only columns: the gantt wizard wires roles to columns
      // whose cell value it must also WRITE (drag-to-reschedule writes
      // start/end, status picker writes the status cell, etc.). Computed
      // types like rollup / formula / doc-reference expose a `readonly$`
      // that stays `true` even in an otherwise editable view — binding
      // them would produce a picker that silently no-ops on every edit.
      // Title columns also report readonly in some layouts and belong to
      // the row-identity channel, not a gantt role.
      if (property.readonly$.value) continue;
      const type = property.type$.value;
      if (type === 'date') date.push(property);
      else if (type === 'progress') progress.push(property);
      else if (type === 'select') status.push(property);
      else if (type === 'member') assignee.push(property);
      else if (type === 'relation' && isSelfRelation(property, selfBlockId)) {
        relation.push(property);
      }
    }

    const start = matchByName(date, NAME_PATTERNS.start);
    let end = matchByName(date, NAME_PATTERNS.end);
    // Avoid picking the same column as both start and end — if the name
    // matches both (e.g. "Due Start"), pin it to start and reach for a
    // distinct end match.
    if (start && end && start.id === end.id) {
      end = matchByName(date, NAME_PATTERNS.end, p => p.id !== start.id);
    }

    // A single well-typed column auto-matches without a naming hint;
    // multiple fall back to name match. No columns or no match leaves
    // the role undetected (section stays off by default).
    const progressMatch =
      (progress.length === 1 ? progress[0] : undefined) ??
      matchByName(progress, NAME_PATTERNS.progress);
    const statusMatch =
      matchByName(status, NAME_PATTERNS.status) ??
      (status.length === 1 ? status[0] : undefined);
    const assigneeMatch =
      (assignee.length === 1 ? assignee[0] : undefined) ??
      matchByName(assignee, NAME_PATTERNS.assignee);

    const match: DetectedMatches = {
      start,
      end,
      progress: progressMatch,
      status: statusMatch,
      assignee: assigneeMatch,
      dependencies: matchByName(relation, NAME_PATTERNS.dependencies),
    };

    return {
      date,
      progress,
      status,
      assignee,
      relation,
      selfBlockId,
      match,
    };
  }

  static wizardCase(detected: DetectedColumns): WizardCase {
    if (detected.date.length === 0) return 'no-date';
    if (detected.date.length === 1) return 'one-date';
    return 'many-dates';
  }
}

function matchByName(
  properties: readonly Property[],
  pattern: RegExp,
  extraFilter?: (property: Property) => boolean
): Property | undefined {
  for (const property of properties) {
    if (!pattern.test(property.name$.value)) continue;
    if (extraFilter && !extraFilter(property)) continue;
    return property;
  }
  return undefined;
}

/**
 * `DatabaseBlockDataSource` exposes `blockId` as a streamlain extension;
 * the generic `DataSource` interface doesn't. We read it reflectively
 * so the wizard stays usable in test harnesses that wire a
 * plain-object data source without a BlockSuite model.
 */
function resolveSelfBlockId(view: GanttSingleView): string {
  const source = (
    view.manager as unknown as { dataSource?: { blockId?: string } }
  ).dataSource;
  return source?.blockId ?? '';
}

function isSelfRelation(property: Property, selfBlockId: string): boolean {
  if (!selfBlockId) return false;
  const data = property.data$.value as
    | { targetDatabaseId?: string; targetDocId?: string }
    | undefined;
  return data?.targetDatabaseId === selfBlockId;
}
